// Local state of one decision card: editable draft values (saved on blur), variants, the armed
// action (arm → confirm for irreversible ones; reversible ones run on one press with Undo), the
// snooze picker and busy flags. When `active`, it also binds the card keys (1/2/3, s, a, d, h, e,
// v, t, r, Enter, Esc) through the command registry.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CardView, ComposeRemindDays } from "@shared/api";
import type { CardAction } from "@shared/core";
import { isApiError } from "@web/lib/api";
import { feedApi } from "@web/lib/feedApi";
import { acceptCard, feedKeys, invalidateBoards } from "@web/lib/feedQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import type { RemindChoice } from "@web/compose/RemindToggle";
import { useReplyCommands } from "@web/compose/ReplyActions";
import { useCommand } from "@web/shell/commands";
import { makeTimeStore } from "@web/day/makeTimeStore";
import { captureTaskFromCard } from "@web/tasks/taskOps";
import { actionForKey, CONFIRM_GUARD_MS, isExternal, pressStep, sendAction } from "../actions";
import { cancelWork, dismissCard, runCardAction, snoozeCard, type CardOutcome } from "../cardOps";
import { recommendedAction } from "../recommended";
import { isToReview } from "../selectors";
import { needsReply } from "@shared/drafts";
import { placeholderMessage } from "@shared/placeholders";
import { HOLDING_BLOCK_ID, type HoldingReplyView } from "@shared/unstick";
import { unstickKeys } from "@web/lib/unstickApi";
import { askForDraft } from "./draftAhead";
import { unfilledDraft } from "./draftWords";
import { holdingArmBlock, holdingEditor, useHoldingReplyKey } from "./holdingReply";
import { focusIntentChips } from "./IntentChips";
import { cardEditor, focusPlaceholder } from "./PlaceholderPrompt";

const UPDATE_PENDING =
  "A newer version of this card waits for the next round. Start it to act on the latest.";

interface Armed {
  actionId: string;
  at: number;
  /** The card revision on screen when the action was armed. */
  rev: number;
}

export interface CardControllerOptions {
  active: boolean;
  onOutcome?: ((cardId: string, outcome: CardOutcome) => void) | undefined;
  onToggleThread?: (() => void) | undefined;
  /** Esc with nothing armed (e.g. close the thread drawer). Return true if it did something. */
  onEscape?: (() => boolean) | undefined;
  /** Whether onEscape currently has something to close. */
  canEscape?: boolean | undefined;
}

function editableValues(card: CardView | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const block of card?.blocks ?? []) {
    if (block.type === "editable_text") out[block.id] = block.value ?? block.text ?? "";
  }
  return out;
}

export function useCardController(card: CardView | undefined, options: CardControllerOptions) {
  const queryClient = useQueryClient();
  const cardId = card?.id ?? null;
  const [values, setValues] = useState<Record<string, string>>(() => editableValues(card));
  const [variantIndex, setVariantIndex] = useState<Record<string, number>>({});
  const [armed, setArmed] = useState<Armed | null>(null);
  const [busy, setBusy] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const server = useRef<Record<string, string>>(editableValues(card));
  const rev = useRef(card?.rev ?? 0);
  const saving = useRef<Promise<unknown> | null>(null);
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // A different card: start fresh. The same card with new server data: keep what the reader
  // is editing, take the server's value everywhere else.
  const lastCardId = useRef(cardId);
  useEffect(() => {
    if (!card) return;
    const fresh = editableValues(card);
    if (lastCardId.current !== card.id) {
      lastCardId.current = card.id;
      server.current = fresh;
      rev.current = card.rev;
      setValues(fresh);
      setVariantIndex({});
      setArmed(null);
      setSnoozeOpen(false);
      return;
    }
    const previous = server.current;
    server.current = fresh;
    rev.current = card.rev;
    setValues((local) => {
      const next = { ...local };
      for (const [id, value] of Object.entries(fresh)) {
        if (local[id] === undefined || local[id] === previous[id]) next[id] = value;
      }
      return next;
    });
  }, [card]);

  // A card that changed under an armed action must be looked at again: the approval binds to
  // exactly what was on screen when it was armed.
  useEffect(() => {
    if (!armed || !card) return;
    if (!isToReview(card)) setArmed(null);
    else if (card.rev !== armed.rev) {
      setArmed(null);
      toast("This card just changed. Look again before confirming.");
    }
  }, [armed, card]);

  const saveBlock = useCallback(
    async (blockId: string): Promise<void> => {
      if (!cardId) return;
      const value = valuesRef.current[blockId];
      if (value === undefined || value === server.current[blockId]) return;
      const run = async () => {
        try {
          const next = await feedApi.saveBlock(cardId, blockId, value, rev.current);
          server.current = { ...server.current, [blockId]: value };
          rev.current = next.rev;
          acceptCard(queryClient, next);
        } catch (error) {
          if (isApiError(error, "stale") || isApiError(error, "conflict")) {
            toast("This card changed while you were editing. Your text is kept.");
            void queryClient.invalidateQueries({ queryKey: feedKeys.card(cardId), exact: true });
          } else {
            toast("Couldn't save your edit yet. It's kept here; try again.");
          }
          throw error;
        }
      };
      const previous = saving.current ?? Promise.resolve();
      const next = previous.catch(() => undefined).then(run);
      saving.current = next;
      try {
        await next;
      } finally {
        if (saving.current === next) saving.current = null;
      }
    },
    [cardId, queryClient],
  );

  /** Save every edited block; resolves when the server has the text the reader sees. */
  const flushSaves = useCallback(async (): Promise<boolean> => {
    try {
      for (const id of Object.keys(valuesRef.current)) await saveBlock(id);
      return true;
    } catch {
      return false;
    }
  }, [saveBlock]);

  const setValue = useCallback((blockId: string, value: string) => {
    setArmed(null); // the artifact changed: an approval must be armed again on the new text
    setValues((v) => ({ ...v, [blockId]: value }));
  }, []);

  const variantDrafts = useRef<Record<string, string>>({});
  const chooseVariant = useCallback(
    (blockId: string, index: number) => {
      const block = card?.blocks.find((b) => b.id === blockId);
      const variants = block?.variants ?? [];
      const target = variants[index];
      if (!target) return;
      const current = variantIndex[blockId] ?? 0;
      if (current === index) return;
      variantDrafts.current[`${blockId}:${current}`] = valuesRef.current[blockId] ?? "";
      setVariantIndex((v) => ({ ...v, [blockId]: index }));
      setValue(blockId, variantDrafts.current[`${blockId}:${index}`] ?? target.value);
    },
    [card, variantIndex, setValue],
  );

  const cycleVariant = useCallback(() => {
    const block = card?.blocks.find(
      (b) => b.type === "editable_text" && (b.variants?.length ?? 0) > 1,
    );
    if (!block) {
      toast("This card has only one version of the draft.");
      return;
    }
    const count = block.variants!.length;
    chooseVariant(block.id, ((variantIndex[block.id] ?? 0) + 1) % count);
  }, [card, variantIndex, chooseVariant]);

  const actionable = !!card && isToReview(card);
  const armedAction = useMemo(
    () => (armed && card ? (card.actions.find((a) => a.id === armed.actionId) ?? null) : null),
    [armed, card],
  );

  const blockValues = () => ({ ...valuesRef.current });

  // "Remind me if they don't reply" on a send: the owner's switch in the strip, else the strip's
  // default once the server's preview says who it goes to (unknown: the tracker decides).
  const [remindChoice, setRemindChoice] = useState<RemindChoice | null>(null);
  const remindDays = useRef<ComposeRemindDays | undefined>(undefined);
  const setRemindDays = useCallback((days: ComposeRemindDays | undefined) => {
    remindDays.current = days;
  }, []);

  /** Run an action. `boundRev` pins an armed approval to the revision that was on screen. */
  const execute = useCallback(
    async (action: CardAction, boundRev?: number) => {
      if (!card) return;
      setBusy(true);
      try {
        if (boundRev === undefined) await flushSaves();
        const fresh = queryClient.getQueryData<CardView>(feedKeys.card(card.id)) ?? card;
        const cardRev = boundRev ?? rev.current;
        const outcome = await runCardAction(queryClient, { ...fresh, rev: cardRev }, action, {
          blockValues: blockValues(),
          ...(isSendAction(action) ? { remindDays: remindDays.current } : {}),
        });
        if (outcome) options.onOutcome?.(card.id, outcome);
      } finally {
        setBusy(false);
        setArmed(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card, flushSaves, queryClient, options.onOutcome],
  );

  const arm = useCallback(
    async (action: CardAction) => {
      if (!card || !actionable || busy) return;
      if (card.updatePending) {
        toast(UPDATE_PENDING);
        return;
      }
      // U4 placeholder gate: nothing arms while the draft still says [[fill: …]].
      const unfilled = unfilledDraft(action, card.blocks, valuesRef.current);
      if (unfilled) {
        toast(placeholderMessage(unfilled.text));
        focusPlaceholder(cardEditor(card.id, unfilled.blockId));
        return;
      }
      // U5: a holding reply arms only while its text names the day its task will be due.
      const holding = queryClient.getQueryData<{ holding: HoldingReplyView | null }>(
        unstickKeys.holding(card.id),
      )?.holding;
      const dayNote = holdingArmBlock(action.id, holding, valuesRef.current[HOLDING_BLOCK_ID]);
      if (dayNote) {
        toast(dayNote);
        holdingEditor(card.id)?.focus();
        return;
      }
      setSnoozeOpen(false);
      // The approval binds to the text on screen: save edits first, then arm.
      const saved = await flushSaves();
      if (!saved) return;
      setRemindChoice(null);
      remindDays.current = undefined;
      setArmed({ actionId: action.id, at: Date.now(), rev: rev.current });
    },
    [card, actionable, busy, flushSaves, queryClient],
  );

  const confirm = useCallback(() => {
    if (!armedAction || busy) return;
    if (!armed || Date.now() - armed.at < CONFIRM_GUARD_MS) return;
    void execute(armedAction, armed.rev);
  }, [armedAction, armed, busy, execute]);

  const disarm = useCallback(() => setArmed(null), []);

  /** `reason`: U5 "Let it go" keeps why with the dismissal. */
  const dismiss = useCallback(
    async (reason?: string) => {
      if (!card || !actionable) return;
      setArmed(null);
      const ok = await dismissCard(queryClient, card, reason);
      if (ok) options.onOutcome?.(card.id, "decided");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card, actionable, queryClient, options.onOutcome],
  );

  const snooze = useCallback(
    async (until: string, label: string) => {
      if (!card || !actionable) return;
      setSnoozeOpen(false);
      setArmed(null);
      const ok = await snoozeCard(queryClient, card, until, label);
      if (ok) options.onOutcome?.(card.id, "snoozed");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card, actionable, queryClient, options.onOutcome],
  );

  /**
   * `t` (or the card's "make a task" action): a local capture, never an approval. The card stays
   * where it is; the new task_ref block and an undo toast show what happened.
   */
  const capturing = useRef(false);
  const captureTask = useCallback(async () => {
    if (!card || capturing.current) return;
    capturing.current = true;
    setArmed(null);
    setSnoozeOpen(false);
    try {
      const fresh = queryClient.getQueryData<CardView>(feedKeys.card(card.id)) ?? card;
      await captureTaskFromCard(queryClient, fresh, {
        useAction: actionable && !fresh.updatePending,
      });
      // U6: a promise card's reminder settles the card, so the round moves on like any decision.
      const after = queryClient.getQueryData<CardView>(feedKeys.card(card.id));
      if (actionable && after?.status === "done") options.onOutcome?.(card.id, "decided");
    } finally {
      capturing.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, actionable, queryClient, options.onOutcome]);

  /**
   * `r`: ask Claude to research what this card is about. Preparation, never an approval: the card
   * shows the work under way (on the board it waits under "Claude's on it"), then comes back with
   * the research block. Undo cancels it while it is still queued.
   */
  const researching = useRef(false);
  const research = useCallback(async () => {
    if (!card || researching.current) return;
    researching.current = true;
    setArmed(null);
    setSnoozeOpen(false);
    try {
      const result = await feedApi.research(card.id);
      acceptCard(queryClient, result.card);
      invalidateBoards(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["work"] });
      if (!result.queued) {
        toast("Claude is already looking this up.");
        return;
      }
      registerUndo({
        message: "Claude is looking this up. The answer will appear on this card.",
        undo: () => cancelWork(queryClient, result.work.id),
        doneMessage: "Research cancelled.",
        tooLateMessage: "Too late to cancel: Claude already started.",
      });
    } catch (error) {
      toast(
        isApiError(error, "conflict")
          ? "Claude is already working on this card. Ask again once it's done."
          : isApiError(error, "invalid_request") && error.serverMessage
            ? `Couldn't research this card: ${error.serverMessage}.`
            : "Couldn't ask for research just now. Nothing changed.",
      );
    } finally {
      researching.current = false;
    }
  }, [card, queryClient]);

  /**
   * A button press or its key: an irreversible external action arms (or confirms when it is
   * already armed); a reversible one runs at once, with Undo, like the local actions.
   */
  const press = useCallback(
    (action: CardAction) => {
      if (!card || !actionable) return;
      switch (pressStep(action, armed, Date.now())) {
        case "arm":
          void arm(action);
          return;
        case "confirm":
          confirm();
          return;
        case "wait":
          return;
        case "run":
          break;
      }
      setArmed(null);
      switch (action.behavior) {
        case "dismiss_card":
          void dismiss();
          return;
        case "snooze":
          setSnoozeOpen((open) => !open);
          return;
        case "create_task":
          void captureTask();
          return;
        default:
          if (busy) return;
          if (isExternal(action) && card.updatePending) {
            toast(UPDATE_PENDING);
            return;
          }
          setSnoozeOpen(false);
          void execute(action);
      }
    },
    [card, actionable, armed, arm, confirm, busy, dismiss, execute, captureTask],
  );

  /** `m` (or the card's Make time button): opens the picker; nothing is added until confirmed. */
  const makeTime = useCallback(() => {
    if (!card) return;
    setArmed(null);
    setSnoozeOpen(false);
    makeTimeStore.open({ target: { kind: "card", id: card.id }, title: card.title });
  }, [card]);

  const pressKey = (key: string) => {
    if (!card) return;
    if (snoozeOpen && /^[1-4]$/.test(key)) {
      document.querySelector<HTMLButtonElement>(`[data-snooze-preset="${key}"]`)?.click();
      return;
    }
    const action = actionForKey(card.actions, key);
    if (action) press(action);
  };

  const focusEditor = () => {
    if (!cardId) return;
    const el = document.querySelector<HTMLTextAreaElement>(
      `[data-card-editor="${CSS.escape(cardId)}"]`,
    );
    if (!el) {
      // U4: `e` on a card that needs a reply and has no draft asks Claude for one.
      if (card && needsReply(card)) void askForDraft(queryClient, card.id);
      else toast("This card has no draft to edit.");
      return;
    }
    setArmed(null);
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  };

  const on = options.active && !!card;
  const keysOn = on && actionable;
  useCommand("card.action1", () => pressKey("1"), keysOn);
  useCommand("card.action2", () => pressKey("2"), keysOn);
  useCommand("card.action3", () => pressKey("3"), keysOn);
  // Space: the highlighted next step (U3), pressed exactly like its 1/2/3 key.
  const recommended = card ? recommendedAction(card.actions) : null;
  useCommand(
    "card.recommended",
    () => {
      if (recommended) press(recommended);
    },
    keysOn && !!recommended,
  );
  useCommand(
    "card.send",
    () => {
      const send = card ? sendAction(card.actions) : null;
      if (send) press(send);
      else toast("Nothing to send on this card.");
    },
    keysOn,
  );
  useCommand(
    "card.archive",
    () => {
      const cleanup = card?.actions.find((a) => a.behavior === "default_cleanup");
      if (cleanup) press(cleanup);
      else toast("This card has no archive action.");
    },
    keysOn,
  );
  useCommand("card.dismiss", () => void dismiss(), keysOn);
  useCommand(
    "card.snooze",
    () => {
      const hasSnooze = card?.actions.some((a) => a.behavior === "snooze");
      if (hasSnooze) setSnoozeOpen((open) => !open);
    },
    keysOn,
  );
  useCommand("card.task", () => void captureTask(), on);
  // U5: `b` writes a holding reply ("Got this, I'll get back to you by Friday").
  const holdingReply = useHoldingReplyKey(card, keysOn);
  useCommand("card.research", () => void research(), keysOn);
  useCommand("card.makeTime", makeTime, on);
  // Reply, Reply all, Forward the card's thread yourself, in the compose sheet.
  useReplyCommands(on ? (card?.threadKey ?? null) : null);
  useCommand("card.edit", focusEditor, keysOn);
  useCommand("card.variant", cycleVariant, keysOn);
  const intents = !!card?.blocks.some((b) => b.intents && (b.variants?.length ?? 0) > 1);
  useCommand("card.intent", () => void (cardId && focusIntentChips(cardId)), keysOn && intents);
  useCommand("card.confirm", confirm, on && !!armedAction);
  useCommand("card.thread", () => options.onToggleThread?.(), on && !!options.onToggleThread);
  useCommand(
    "card.cancel",
    () => {
      if (snoozeOpen) setSnoozeOpen(false);
      else if (armed) setArmed(null);
      else options.onEscape?.();
    },
    on && (snoozeOpen || !!armed || !!options.canEscape),
  );

  return {
    values,
    setValue,
    saveBlock,
    flushSaves,
    variantIndex,
    chooseVariant,
    armedAction,
    arm,
    confirm,
    disarm,
    remindChoice,
    setRemindChoice,
    setRemindDays,
    press,
    busy,
    snoozeOpen,
    setSnoozeOpen,
    snooze,
    actionable,
    captureTask,
    makeTime,
    dismiss,
    holdingReply,
  };
}

export type CardController = ReturnType<typeof useCardController>;

function isSendAction(action: CardAction): boolean {
  return action.actionKind === "gmail.send_reply" || action.actionKind === "gmail.forward";
}
